import React from "react"

import { useTranslation } from "react-i18next"

import { Grid } from "@mui/material"
import type { PropertyAccessor } from "@nivo/core"
import type { ComputedCell } from "@nivo/heatmap"
import { ResponsiveHeatMap } from "@nivo/heatmap"
import { Typography } from "@synapse-analytics/synapse-ui"

import { getTheme } from "../../../hooks/UseTheme"
import { NivoTheme } from "../../../themes/nivoTheme"
import type { MetricsState } from "../../../types/custom/projects"
import { ChartsContainer } from "../ChartsContainer"

export interface HeatMapData {
  id: string
  data: Array<{ x: string; y: number | null }>
  isLoading?: boolean
}

type Props = {
  title: string
  data: HeatMapData[]
  padding?: string
  graphHeight: number
  emptyState?: MetricsState | undefined
  margin?: Partial<{ top: number; right: number; bottom: number; left: number }>
  range?: number
  isLoading: boolean
  tooltipText?: string | React.ReactElement
  label?:
    | PropertyAccessor<
        Omit<
          ComputedCell<{
            x: string
            y: number | null
          }>,
          "label" | "borderColor" | "color" | "opacity" | "labelTextColor"
        >,
        string
      >
    | undefined
  formatTopLabelFn?: (v: string) => string
  formatLeftLabelFn?: (v: string) => string
}

/**
 * ConfusionMatrix component renders a heatmap visualization to represent
 * a confusion matrix using Nivo's `ResponsiveHeatMap`. It is designed to
 * display classification results, showing the relationship between actual
 * and predicted labels.
 *
 * @param {Props} props - The properties for configuring the confusion matrix
 *        visualization.
 * @param {string} props.title - The title for the chart container.
 * @param {HeatMapData[]} props.data - The data to be visualized as a heatmap.
 * @param {number} props.graphHeight - The height of the graph in pixels.
 * @param {boolean} props.isLoading - Flags if the data is still loading.
 * @param {string} [props.tooltipText] - Optional text for the tooltip.
 * @param {object} props.margin - Margins for the heatmap chart.
 * @param {string} [props.rightLegend] - Legend text for the right axis.
 * @param {string} [props.bottomLegend] - Legend text for the bottom axis.
 * @param {PropertyAccessor} [props.label] - Custom label accessor function.
 * @param {(v: string) => string} [props.formatTopLabelFn] - Formatter for top axis labels.
 * @param {(v: string) => string} [props.formatLeftLabelFn] - Formatter for left axis labels.
 * @returns {React.ReactElement} The rendered confusion matrix component.
 */

export function ConfusionMatrix(props: Props): React.ReactElement {
  const { t } = useTranslation("graphs")
  const { title, data, graphHeight, isLoading, tooltipText, margin, label, formatTopLabelFn, formatLeftLabelFn } = props

  const theme = getTheme()

  return (
    <ChartsContainer
      title={title}
      isLoading={isLoading}
      graphHeight={graphHeight}
      tooltipText={tooltipText}
      gutterBottom
    >
      {!data || data.length < 1 ? (
        <Grid container direction="row" justifyContent="center" alignItems="center" className={"full-height"}>
          <Typography variant="p" variantColor={2}>
            {t("confusionMatrix.emptyState.noPredictionsOrFeedback")}
          </Typography>
        </Grid>
      ) : (
        <ResponsiveHeatMap
          data={data}
          theme={NivoTheme(theme)}
          margin={margin}
          axisTop={{
            tickSize: 5,
            tickPadding: 5,
            tickRotation: 0,
            legend: "",
            legendOffset: 46,
            format: formatTopLabelFn,
          }}
          axisRight={{
            tickSize: 0,
            legendPosition: "middle",
            legendOffset: 20,
            //disable ticks
            format: () => "",
          }}
          axisLeft={{
            tickSize: 5,
            tickPadding: 5,
            tickRotation: 0,
            legendPosition: "middle",
            legendOffset: -72,
            format: formatLeftLabelFn,
          }}
          axisBottom={{
            tickSize: 0,
            legendPosition: "middle",
            legendOffset: 20,
            //disable ticks
            format: () => "",
          }}
          label={label}
          colors={{
            type: "diverging",
            scheme: "blues",
            divergeAt: 0,
          }}
          emptyColor={theme.palette.gray.background[1]}
          borderRadius={2}
          inactiveOpacity={1}
          borderColor={{ theme: "background" }}
          labelTextColor={{ theme: "labels.text.fill" }}
          tooltip={({ cell: { color, id, value } }) => (
            <div className={"chart-tooltip"}>
              <Grid container direction="row" justifyContent="flex-start" alignItems="center" spacing={1}>
                <Grid item>
                  <span key="chip" style={{ display: "block", width: "12px", height: "12px", background: color }} />
                </Grid>
                <Grid item>
                  <Typography variant="label-bold" variantColor={2}>
                    {t("confusionMatrix.tooltip.value", { id: id.replace(".", " - "), value })}
                  </Typography>
                </Grid>
              </Grid>
            </div>
          )}
        />
      )}
    </ChartsContainer>
  )
}
